/**
 * Character Arc Analysis — Visualizes character transformation across the
 * screenplay's three-act structure with trajectory charts and scoring cards.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { ChevronDown, ChevronUp, AlertTriangle, TrendingUp } from 'lucide-react';

const ARC_COLORS = ['#F59E0B', '#14B8A6', '#EC4899', '#A855F7', '#10B981'];

const ARC_TYPE_LABELS = {
  transformation: 'Transformation',
  flat: 'Flat Arc',
  negative: 'Negative Arc',
  circular: 'Circular',
};

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function scoreBgHex(score) {
  if (score >= 80) return '#4CAF50';
  if (score >= 60) return '#FFD700';
  if (score >= 40) return '#FF8C42';
  return '#DC143C';
}

function ScoreBar({ label, score }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-[var(--text-muted)] w-24 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-app)]">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: scoreBgHex(score) }}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
      <span className="text-[10px] font-medium text-[var(--text-secondary)] w-6 text-right">{score}</span>
    </div>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-2.5 shadow-lg text-xs max-w-[220px]">
      {point.label && (
        <p className="font-medium text-[var(--text-primary)] mb-0.5">{point.label}</p>
      )}
      <p className="text-[var(--text-muted)] text-[10px]">Scene {point.scene}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-1.5 mt-1">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-[var(--text-secondary)]">{entry.name}: {entry.value}</span>
        </div>
      ))}
      {point.description && (
        <p className="text-[var(--text-muted)] text-[10px] mt-1 border-t border-[var(--border-subtle)] pt-1">
          {point.description}
        </p>
      )}
    </div>
  );
}

function ArcCard({ arc, color, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${scoreColor(arc.overall_arc_score)}`}
            style={{ backgroundColor: `${scoreBgHex(arc.overall_arc_score)}15` }}
          >
            {arc.overall_arc_score}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--text-primary)]">{arc.character}</span>
              <span
                className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium"
                style={{ backgroundColor: `${color}15`, color }}
              >
                {ARC_TYPE_LABELS[arc.arc_type] || arc.arc_type}
              </span>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
              Magnitude {arc.transformation_magnitude} · Believability {arc.transformation_believability}
            </p>
          </div>
        </div>
        {expanded
          ? <ChevronUp size={14} className="text-[var(--text-muted)]" />
          : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              {/* Score Bars */}
              <div className="space-y-1.5">
                <ScoreBar label="Magnitude" score={arc.transformation_magnitude} />
                <ScoreBar label="Believability" score={arc.transformation_believability} />
                <ScoreBar label="Completeness" score={arc.arc_completeness} />
                <ScoreBar label="Thematic Res." score={arc.thematic_resonance} />
                <ScoreBar label="Plot Necessity" score={arc.plot_necessity} />
                <ScoreBar label="Emotional Payoff" score={arc.emotional_payoff} />
              </div>

              {/* Assessment */}
              {arc.assessment && (
                <p className="text-xs text-[var(--text-secondary)]">{arc.assessment}</p>
              )}

              {/* Red Flags */}
              {arc.red_flags?.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-[10px] font-medium text-red-400 flex items-center gap-1">
                    <AlertTriangle size={10} /> Red Flags
                  </span>
                  {arc.red_flags.map((flag, i) => (
                    <div
                      key={i}
                      className="text-[11px] text-red-300 pl-2 border-l-2 border-red-500/30 bg-red-500/5 rounded-r px-2 py-1"
                    >
                      {flag}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CharacterArcAnalysis({ data }) {
  const arcs = data?.arcs || [];

  const [visibleArcs, setVisibleArcs] = useState(() =>
    Object.fromEntries(arcs.map((a) => [a.character, true]))
  );

  const actStructure = data.act_structure || {};

  // Build unified data points keyed by scene number
  const sceneMap = {};
  arcs.forEach((arc) => {
    arc.trajectory?.forEach((pt) => {
      if (!sceneMap[pt.scene]) {
        sceneMap[pt.scene] = { scene: pt.scene, label: pt.label, description: pt.description };
      }
      sceneMap[pt.scene][arc.character] = pt.state;
    });
  });
  const chartData = Object.values(sceneMap).sort((a, b) => a.scene - b.scene);

  const toggleArc = (character) => {
    setVisibleArcs((prev) => ({ ...prev, [character]: !prev[character] }));
  };

  if (!arcs.length) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-1.5">
          <TrendingUp size={14} className="text-amber-400" />
          Character Arc Analysis
        </h3>
        <span className={`text-sm font-bold ${scoreColor(data.overall_arc_score)}`}>
          Arc Score: {data.overall_arc_score}/100
        </span>
      </div>

      {/* Toggle checkboxes */}
      <div className="flex flex-wrap gap-3">
        {arcs.map((arc, i) => {
          const color = ARC_COLORS[i % ARC_COLORS.length];
          return (
            <label
              key={arc.character}
              className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-[var(--text-secondary)]"
            >
              <input
                type="checkbox"
                checked={!!visibleArcs[arc.character]}
                onChange={() => toggleArc(arc.character)}
                className="sr-only"
              />
              <span
                className="w-3 h-3 rounded-sm border flex items-center justify-center"
                style={{
                  borderColor: color,
                  backgroundColor: visibleArcs[arc.character] ? color : 'transparent',
                }}
              >
                {visibleArcs[arc.character] && (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M1.5 4L3 5.5L6.5 2" stroke="#000" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              {arc.character}
            </label>
          );
        })}
      </div>

      {/* Trajectory Chart */}
      <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="3 3" />
            <XAxis
              dataKey="scene"
              tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--border-subtle)' }}
              tickLine={false}
              label={{ value: 'Scene', position: 'insideBottomRight', offset: -5, fill: 'var(--text-muted)', fontSize: 10 }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--border-subtle)' }}
              tickLine={false}
              label={{ value: 'State', angle: -90, position: 'insideLeft', fill: 'var(--text-muted)', fontSize: 10 }}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Act structure dividers */}
            {actStructure.act1_end && (
              <ReferenceLine
                x={actStructure.act1_end}
                stroke="var(--text-muted)"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
                label={{ value: 'Act 1', position: 'top', fill: 'var(--text-muted)', fontSize: 9 }}
              />
            )}
            {actStructure.act2a_end && (
              <ReferenceLine
                x={actStructure.act2a_end}
                stroke="var(--text-muted)"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
                label={{ value: 'Mid', position: 'top', fill: 'var(--text-muted)', fontSize: 9 }}
              />
            )}
            {actStructure.act3_start && (
              <ReferenceLine
                x={actStructure.act3_start}
                stroke="var(--text-muted)"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
                label={{ value: 'Act 3', position: 'top', fill: 'var(--text-muted)', fontSize: 9 }}
              />
            )}

            {/* Character lines */}
            {arcs.map((arc, i) => {
              if (!visibleArcs[arc.character]) return null;
              const color = ARC_COLORS[i % ARC_COLORS.length];
              return (
                <Line
                  key={arc.character}
                  type="monotone"
                  dataKey={arc.character}
                  name={arc.character}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 4, fill: color, stroke: 'var(--bg-elevated)', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: color, stroke: '#fff', strokeWidth: 2 }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Character Arc Cards */}
      <div className="space-y-2">
        {arcs.map((arc, i) => (
          <ArcCard
            key={arc.character}
            arc={arc}
            color={ARC_COLORS[i % ARC_COLORS.length]}
            defaultExpanded={i === 0}
          />
        ))}
      </div>

      {/* Thematic Coherence */}
      {data.thematic_coherence && (
        <p className="text-xs text-[var(--text-secondary)] p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)]">
          {data.thematic_coherence}
        </p>
      )}
    </div>
  );
}
